import React, { useState, useRef, useEffect, Dispatch } from "react";
import {
  VStack,
  HStack,
  Text,
  Button,
  Box,
  Pressable,
  Heading,
  Spinner,
} from "native-base";
import { Dimensions } from "react-native";
import { RootStateOrAny, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUser, setEntitydata } from "../../Redux/Actions/userActions";
import * as AV from "../../AVCore/AVCore";
import { Loc } from "../../App/Loc";
import { DollarCoinsIcon, MyPlanIcon } from "../../assets/icons/icon-assets";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import AVInputtext from "../../components/AVInputtext";
import { PageLoader } from "../../components/loaders";
import EntityProfilePic from "../common/EntityProfilePic";
import AVCancel from "../../components/common/AVCancel";

interface Props {
  entity?: AV.IAVEntity;
  setEntity?: Dispatch<React.SetStateAction<AV.IAVEntity>>;
  setEntityData?: Dispatch<React.SetStateAction<AV.IAVEntity>>;
  sharedPlan?: AV.ISharedPlan;
  setSharedPlan?: (plan: AV.ISharedPlan) => void;
  showCreditsPopup: boolean;
  setShowCreditsPopup: (val: boolean) => void;
  triggerBuyCredits?: boolean;
  setTriggerBuyCredits?: (val: boolean) => void;
  isBuyStorage?: boolean; // flag to differentiate storage vs credits
  boostPostOwner?: AV.IFK | undefined;
}

declare function appendHelcimPayIframe(
  checkoutToken: string,
  showImmediately: boolean,
): void;

interface PaymentMessageData {
  hash: string;
  data: {
    amount: number | string;
    [key: string]: unknown;
  };
}

interface PaymentData {
  checkoutToken: string;
  data: PaymentMessageData;
}

const PurchaseCreditsPopup: React.FC<Props> = ({
  entity,
  setEntity,
  setEntityData,
  sharedPlan,
  setSharedPlan,
  showCreditsPopup,
  setShowCreditsPopup,
  triggerBuyCredits,
  setTriggerBuyCredits,
  isBuyStorage,
  boostPostOwner,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dimension = Dimensions.get("window");
  const dimensions = useWindowDimension();
  const isMobile = dimension?.width <= 500 || false;
  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  ) as AV.IAVEntity;
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );
  const [pageLoader, setPageLoader] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(
    !isBuyStorage ? (triggerBuyCredits ? "$5" : "$2") : "50GBM",
  );
  const [showOtherAmount, setShowOtherAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoadingBuy, setIsLoadingBuy] = useState(false);
  const [selectAmtWithCredits, setSelectAmtWithCredits] = useState(
    triggerBuyCredits ? "23000" : "8000",
  );
  const [isMinValidate, setIsMinValidate] = useState(false);
  const [finalEntity, setFinalEntity] = useState<AV.IAVEntity | undefined>(
    entity ?? currentUser,
  );
  const [billingCycle, setBillingCycle] = React.useState("monthly");

  // Testing Storage SubscriptionLinks
  const testSubscriptionLinks: Record<string, string> = {
    // Monthly
    "50GBM":
      "https://subscriptions.helcim.com/subscribe/f2555ziplq909u32townngass0227",
    "100GBM":
      "https://subscriptions.helcim.com/subscribe/fduk1v0k0idggwj8r8x95pjxig20x",
    "1TBM":
      "https://subscriptions.helcim.com/subscribe/f5jxct5xcsat36ibq2w0tezblsnt9",
    // Yearly
    "50GBY":
      "https://subscriptions.helcim.com/subscribe/fo3dw6mowdijt5sdixflyb9p97kmm",
    "100GBY":
      "https://subscriptions.helcim.com/subscribe/fpfnp58woy7kzajv873ndlcu08fpd",
    "1TBY":
      "https://subscriptions.helcim.com/subscribe/fytx5ljfenhkt6l1zijyd0teax931",
  };

  // Production Storage SubscriptionLinks
  const prodSubscriptionLinks: Record<string, string> = {
    // Monthly
    "50GBM":
      "https://subscriptions.helcim.com/subscribe/flob9po7sq03r89iiiy0qxyz6b3rk",
    "100GBM":
      "https://subscriptions.helcim.com/subscribe/fi4dlpgttao2hqgjft5uwyael5ps9",
    "1TBM":
      "https://subscriptions.helcim.com/subscribe/fqz62pjmbv6dbnr3qssl18s50y142",
    // Yearly
    "50GBY":
      "https://subscriptions.helcim.com/subscribe/fausbicf5brmy4jmschhktvmw8nku",
    "100GBY":
      "https://subscriptions.helcim.com/subscribe/faa57qjavxv681k99k4g0jcas2tzq",
    "1TBY":
      "https://subscriptions.helcim.com/subscribe/fiko1weq6tufdssc37aeczgbd01a7",
  };

  // Pick which set to use
  const hostname = window.location.hostname;
  const isTestEnv =
    hostname === "dev.actavivo.com" ||
    hostname === "qa.actavivo.com" ||
    hostname === "localhost";

  const subscriptionLinks = isTestEnv
    ? testSubscriptionLinks
    : prodSubscriptionLinks;

  const handleBuyNowCredits = async (planId?: string) => {
    try {
      if (isBuyStorage) {
        // Storage: redirect to subscription checkout
        const link = subscriptionLinks[planId || "50GBM"];
        if (link) {
          window.open(link, "_blank");
          //setShowCreditsPopup(false);
        } else {
          console.error("No subscription link found for", planId);
        }
        return;
      }

      // Credits: payment flow
      setIsLoadingBuy(true);

      const amount =
        selectedAmount === Loc.currentLang.activity.other
          ? parseFloat(customAmount)
          : parseFloat(selectedAmount.replace("$", ""));

      if (isNaN(amount) || amount <= 0) {
        throw new Error("Invalid amount selected.");
      }

      // Construct CollectOptions
      const collectOptions: AV.ICollectOptions = {
        collectReason: "C", // "C" for Credits Purchase
        personAmount: amount,
        currency: "USD",
        collectType: "Members",
        showPersonResults: false,
        trackByPerson: false,
        chargeFee: false,
        perPersonFee: false,
        //target: sharedPlan
        //  ? AV.AVEntity.getLink(sharedPlan as AV.ISharedPlan)
        //  : AV.AVEntity.getLink(finalEntity as AV.IAVEntity),
        target: AV.AVEntity.getLink(finalEntity ?? currentUser),
      };

      // Get payment token
      const token = await AV.Pay.createPayment(collectOptions);

      if (token) {
        // Initialize Helcim payment
        await initHelcimPayment(
          token,
          amount,
          currentUser?.email || "",
          currentUser?.name || "",
        );
      } else {
        throw new Error("Failed to create payment token");
      }
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setIsLoadingBuy(false);
      //setShowCreditsPopup(false);
    }
  };

  //#region HelcimPayment Section

  // Add these variables at component level
  const [initData, setInitData] = useState<AV.IPayInitResp | null>(null);
  const [verify, setVerify] = useState<AV.IPayResultResp | null>(null);
  const tokenRef = useRef<string | null>(null);

  // Use ref to keep latest initData for use in event listener
  const initDataRef = useRef<AV.IPayInitResp | null>(null);
  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  // NOTE: Add Helcim script only once globally (public/index.html) – avoid TOKEN_LENGTH redeclare error.

  // Add event listener once on mount
  useEffect(() => {
    window.addEventListener("message", handleHelcimResponse);
    return () => {
      window.removeEventListener("message", handleHelcimResponse);
    };
  }, []);

  const initHelcimPayment = async (
    token: string,
    amount: number,
    email: string,
    name: string,
  ) => {
    try {
      setPageLoader(true);
      tokenRef.current = token;

      const response = await fetch("/api/pay/HInit", {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({ token, amount, email, name }),
      });

      const data = await response.json();
      setInitData(data); // update state
      initDataRef.current = data; // keep ref in sync

      appendHelcimPayIframe(data.checkoutToken, true);

      setTimeout(() => {
        setPageLoader(false);
      }, 5000);
    } catch (error) {
      console.error("Payment initialization failed:", error);
      setPageLoader(false);
    }
  };

  const handleHelcimResponse = async (event: MessageEvent) => {
    const currentInitData = initDataRef.current;
    if (!currentInitData) return;

    const expectedKey = "helcim-pay-js-" + currentInitData.checkoutToken;

    if (event.data.eventName === expectedKey) {
      const status = event.data.eventStatus;
      const message = event.data.eventMessage;

      if (status === "ABORTED") {
        console.warn("Transaction aborted:", message);
      }

      if (status === "SUCCESS") {
        console.log("Transaction success:", message);
        try {
          const response = JSON.parse(message).data;

          await handlePaymentResult({
            checkoutToken: currentInitData.checkoutToken,
            data: response,
          });
        } catch (err) {
          console.error(
            "Failed parsing or handling Helcim success message:",
            err,
          );
        }
      }
    }
  };

  const handlePaymentResult = async (paymentData: PaymentData) => {
    try {
      const payload = {
        token: tokenRef.current ?? "",
        checkoutToken: paymentData.checkoutToken,
        data: {
          hash: paymentData.data.hash,
          data: JSON.stringify(paymentData.data.data),
        },
      };

      const response = await fetch("/api/pay/HResult", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("HResult response:", response);
      const result = await response.json();
      console.log("Payment verification result:", result);
      setVerify(result);

      if (result?.status === "OK") {
        console.log("Payment verified successfully");
      } else {
        console.warn("Payment verification failed on backend");
      }

      //try {
      //  const Fk = AV.AVEntity.getFk(finalEntity || currentUser);
      //  const sharedPlan: AV.ISharedPlan = await AV.AVEntity.getSharedPlan(Fk);
      //  setSharedPlan?.(sharedPlan);
      //} catch (err) {
      //  console.error("Failed to load sharedPlan", err);
      //}

      const amount = Number(paymentData?.data?.data?.amount) || 0;
      const creditToAdd = amount * 5000 - 2000;

      const fk: AV.IFK = AV.AVEntity.getFk(entity ?? currentUser);
      let finalFk: AV.IFK = boostPostOwner ?? fk;

      if (boostPostOwner?.type === "Person") {
        finalFk =
          currentUser?.id !== boostPostOwner.id
            ? AV.AVEntity.getFk(currentUser)
            : boostPostOwner;
      }

      // Event use EVENT OWNER (parent)
      if (finalFk?.type === "Event") {
        let parent: AV.ILink | null = null;

        if (entity?.parent) {
          parent = entity.parent;
        } else {
          const evtEntity =
            entityDataList?.find((x) => x?.id === entity?.id) ?? null;

          if (evtEntity) {
            parent = evtEntity.parent ?? entity;
          } else {
            const evtEntityAPI: AV.IAVEntity | null =
              await AV.AVEntity.get(finalFk);

            if (evtEntityAPI) {
              entityDataList?.push(evtEntityAPI);
              dispatch(setEntitydata(entityDataList));
              parent = evtEntityAPI.parent ?? null;
            }
          }
        }

        if (parent) {
          finalFk = parent;
        }
      }

      // Update current user credits
      if (
        finalFk?.id === currentUser?.id &&
        currentUser?.plan &&
        !currentUser?.plan?.shared
      ) {
        dispatch(
          setUser({
            ...currentUser,
            plan: {
              ...currentUser.plan,
              credits: (currentUser.plan.credits || 0) + creditToAdd,
            },
          }),
        );
      }

      if (finalFk?.id === entity?.id && entity?.plan && !entity?.plan?.shared) {
        setEntity?.({
          ...entity,
          plan: {
            ...entity.plan,
            credits: (entity.plan.credits || 0) + creditToAdd,
          },
        });

        setEntityData?.({
          ...entity,
          plan: {
            ...entity.plan,
            credits: (entity.plan.credits || 0) + creditToAdd,
          },
        });
      } else if (
        finalFk?.id === currentUser?.id &&
        currentUser?.plan &&
        !currentUser?.plan?.shared
      ) {
        const updatedCurrentUser = { ...currentUser };
        const currentPlan = updatedCurrentUser.plan;
        updatedCurrentUser.plan = {
          credits: (currentPlan?.credits || 0) + creditToAdd,
          storeLimit: currentPlan?.storeLimit ?? 0,
          baseStoreLimit: currentPlan?.baseStoreLimit ?? 0,
          payStoreEndDate: currentPlan?.payStoreEndDate ?? new Date(),
          topEntities: currentPlan?.topEntities ?? 0,
          storeUsed: currentPlan?.storeUsed ?? 0,
          shared: currentPlan?.shared ?? { id: "" },
        };

        setEntity?.(updatedCurrentUser);
        setEntityData?.(updatedCurrentUser);
      }

      // Shared Plan
      if (sharedPlan && sharedPlan?.plan) {
        setSharedPlan?.({
          ...sharedPlan,
          plan: {
            ...sharedPlan.plan,
            credits: (sharedPlan.plan.credits || 0) + creditToAdd,
          },
        });

        const updatedList = [...entityDataList];
        const index = updatedList.findIndex((x) => x?.id === sharedPlan?.id);

        if (index !== -1) {
          const current = updatedList[index];

          updatedList[index] = {
            ...current,
            plan: {
              ...current?.plan,
              credits: (current?.plan?.credits || 0) + creditToAdd,
            },
          };
        }

        dispatch(setEntitydata(updatedList));
      }

      // Update entity list credits
      if (
        //finalFk?.id === currentUser?.id &&
        //currentUser?.plan &&
        //!currentUser?.plan?.shared
        !entity?.plan?.shared
      ) {
        const updatedList = [...entityDataList];
        const index = updatedList.findIndex((x) => x?.id === entity?.id);

        if (index !== -1) {
          const current = updatedList[index];

          updatedList[index] = {
            ...current,
            plan: {
              ...current?.plan,
              credits: (current?.plan?.credits || 0) + creditToAdd,
            },
          };
        }

        dispatch(setEntitydata(updatedList));
      }

      setShowCreditsPopup(false);
      // Remove the HelcimPay.js iframe from the DOM
      const iframe = document.querySelector("iframe[src*='helcim']");
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    } catch (error) {
      console.error("Payment verification failed (frontend):", error);
      // Remove the HelcimPay.js iframe from the DOM
      const iframe = document.querySelector("iframe[src*='helcim']");
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  };

  //#endregion HelcimPayment Section

  useEffect(() => {
    //if (!boostPostOwner) return;

    const controller = new AbortController();

    const getEntity = async () => {
      try {
        const fk: AV.IFK = AV.AVEntity.getFk(entity ?? currentUser);
        let finalFk: AV.IFK = boostPostOwner ?? fk;

        const normalizePersonFk = (fk: AV.IFK) =>
          fk?.type === "Person" && fk.id !== currentUser?.id
            ? AV.AVEntity.getFk(currentUser)
            : fk;

        if (boostPostOwner?.type === "Person") {
          finalFk = normalizePersonFk(boostPostOwner);
        }
        finalFk = normalizePersonFk(finalFk);

        // Event Or Channel use OWNER (parent)
        if (finalFk?.type === "Event" || finalFk?.type === "Channel") {
          let parent: AV.ILink | null = null;

          if (entity?.parent) {
            parent = entity.parent;
          } else {
            const evtEntity =
              entityDataList?.find((x) => x?.id === finalFk?.id) ?? null;

            if (evtEntity) {
              parent = evtEntity.parent ?? entity;
            } else {
              const evtEntityAPI: AV.IAVEntity | null =
                await AV.AVEntity.get(finalFk);

              if (evtEntityAPI) {
                entityDataList?.push(evtEntityAPI);
                dispatch(setEntitydata(entityDataList));
                parent = evtEntityAPI.parent ?? null;
              }
            }
          }

          if (parent) {
            finalFk = parent;
          }
        }

        console.log("finalFk: ", finalFk);
        console.log("sharedPlan: ", sharedPlan);

        const entityData =
          entityDataList?.find((x) => x?.id === finalFk.id) ?? null;

        if (entityData) {
          setFinalEntity(entityData);
          return;
        }

        const entityDataAPI = await AV.AVEntity.get(finalFk);

        if (!controller.signal.aborted && entityDataAPI) {
          setFinalEntity(entityDataAPI);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error fetching entity:", error);
        }
      }
    };

    getEntity();

    return () => controller.abort();
  }, [boostPostOwner]);

  if (!showCreditsPopup) return null; // Hide when not visible

  return (
    <Box
      position="fixed"
      bgColor={"AVColor.overlay2"}
      bottom={0}
      right={0}
      top={0}
      left={0}
    >
      {pageLoader && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgColor="#F5F4F1"
          justifyContent="center"
          alignItems="center"
          zIndex={9999}
        >
          <HStack
            space={3}
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="300px"
          >
            <PageLoader />
            {/*<Spinner color="white" size="lg" />*/}
            {/*<Heading color="white" fontSize="md">*/}
            {/*  Loading Payment Gateway...*/}
            {/*</Heading>*/}
          </HStack>
        </Box>
      )}

      <div
        className={`create_post_popup ${
          isMobile && "create_post_popup_mobile"
        }`}
        style={{
          width: isMobile
            ? dimensions.width - 15
            : !isBuyStorage
              ? "400px"
              : "650px",
          height: isBuyStorage ? "350px" : "auto",
          marginTop: "12px",
        }}
      >
        <VStack margin={"24px"} width={"auto"}>
          {/* Header with profile info below on right */}
          <VStack width="100%" mb={4}>
            <Text
              fontSize={"24px"}
              fontWeight={400}
              textAlign={isBuyStorage ? "left" : "center"}
              mb={2}
            >
              {!isBuyStorage
                ? Loc.currentLang.purchaseCredits.purchaseCredits
                : Loc.currentLang.myPlan.purchaseStorage}
            </Text>

            {!isBuyStorage && (
              <HStack
                space={2}
                alignItems="center"
                justifyContent="flex-end"
                alignSelf="flex-end"
                bg="white"
                px={3}
                py={2}
                borderRadius="full"
                shadow={2}
              >
                <Pressable
                  onPress={() => {
                    const target = finalEntity || currentUser;
                    if (!target) return;

                    if (target?.type === "Person" || target?.type === "Pet") {
                      navigate(`/profile/${target.id}/${target.region}`, {
                        state: {
                          user: {
                            id: target.id,
                            pk: target.pk,
                            region: target.region,
                            type: target.type,
                            name: target.name,
                          },
                        },
                      });
                    } else {
                      navigate(`/teams/${target.id}/${target.region}}`, {
                        state: { isCreate: false, team: target },
                      });
                    }
                  }}
                >
                  {(() => {
                    const target = finalEntity || currentUser;
                    const link = AV.AVEntity.getLink(target);

                    return (
                      <EntityProfilePic height={10} width={10} link={link} />
                    );
                  })()}
                </Pressable>
                <VStack maxW="120px">
                  <Text
                    numberOfLines={1}
                    fontSize={14}
                    fontWeight="medium"
                    color="gray.700"
                  >
                    {(() => {
                      const link = AV.AVEntity.getLink(
                        finalEntity || currentUser,
                      );
                      return link?.data || link?.title || "";
                    })()}
                  </Text>
                </VStack>
              </HStack>
            )}

            {isBuyStorage && (
              <Box position="absolute" top={2} right={2}>
                <AVCancel
                  callback={() => {
                    setShowCreditsPopup(false);
                    setTriggerBuyCredits?.(false);
                  }}
                  color={undefined}
                  size={undefined}
                />
              </Box>
            )}
          </VStack>

          {/* Coins Icon and Amount */}
          {!isBuyStorage && (
            <VStack alignItems="center" space={2} mb={4}>
              <Box>
                {!isBuyStorage ? (
                  <DollarCoinsIcon height="64px" width="64px" />
                ) : (
                  <MyPlanIcon height="64px" width="64px" color="#32AE78" />
                )}
              </Box>
              <HStack alignItems="baseline" space={2}>
                <Text fontSize="24px" fontWeight="bold">
                  {Number(selectAmtWithCredits).toLocaleString()}
                </Text>
                <Text fontSize="16px" color="gray.500">
                  {!isBuyStorage
                    ? Loc.currentLang.purchaseCredits.Credits
                    : Loc.currentLang.myPlan.storage}
                </Text>
              </HStack>
              <Text fontSize="14px" color="gray.600" fontWeight="bold">
                {Loc.currentLang.purchaseCredits.bestChoicesForYou}
              </Text>

              {/* Navigation to donation page */}
              <Pressable onPress={() => navigate("/donate")}>
                <Text fontSize="14px" color="blue.500" underline>
                  {Loc.currentLang.purchaseCredits.donationLink}
                </Text>
              </Pressable>
            </VStack>
          )}

          {/* Quick Amount Selection and Storage */}
          <HStack
            justifyContent="center"
            flexWrap="wrap"
            space={2}
            mb={isBuyStorage ? 2 : 4}
          >
            {!isBuyStorage ? (
              <>
                {["$2", "$5", "$10", "$20", Loc.currentLang.activity.other].map(
                  (amount) => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      rounded="full"
                      minW="60px"
                      h="30px"
                      backgroundColor={
                        selectedAmount === amount ? "#00A7BC" : "transparent"
                      }
                      borderColor={
                        selectedAmount === amount ? "#00A7BC" : "gray.300"
                      }
                      _text={{
                        color:
                          selectedAmount === amount ? "white" : "#000000D9",
                        fontSize: "16px",
                      }}
                      _pressed={{
                        backgroundColor:
                          selectedAmount === amount ? "#00A7BC" : "gray.100",
                      }}
                      onPress={() => {
                        setSelectedAmount(amount);
                        if (amount === Loc.currentLang.activity.other) {
                          setShowOtherAmount(true);
                          setCustomAmount("");
                          setSelectAmtWithCredits("0");
                        } else {
                          setShowOtherAmount(false);
                          setCustomAmount("");
                          setIsMinValidate(false);
                          const value = parseInt(amount.replace("$", ""));
                          setSelectAmtWithCredits(
                            (value * 5000 - 2000).toString(),
                          );
                        }
                      }}
                    >
                      {amount}
                    </Button>
                  ),
                )}
              </>
            ) : (
              <VStack space={4} alignItems="center" mb={2}>
                {/* Toggle */}
                <HStack bg="#f0f0f0" rounded="full" p={1} space={1}>
                  <Button
                    variant={billingCycle === "monthly" ? "solid" : "ghost"}
                    bg={
                      billingCycle === "monthly"
                        ? "AVColor.primary"
                        : "transparent"
                    }
                    _text={{
                      color: billingCycle === "monthly" ? "white" : "black",
                    }}
                    rounded="full"
                    px={6}
                    onPress={() => setBillingCycle("monthly")}
                  >
                    {Loc.currentLang.purchaseCredits.monthly}
                  </Button>

                  <Button
                    variant={billingCycle === "yearly" ? "solid" : "ghost"}
                    bg={
                      billingCycle === "yearly"
                        ? "AVColor.primary"
                        : "transparent"
                    }
                    _text={{
                      color: billingCycle === "yearly" ? "white" : "black",
                    }}
                    rounded="full"
                    px={6}
                    onPress={() => setBillingCycle("yearly")}
                  >
                    {Loc.currentLang.purchaseCredits.yearly}
                  </Button>
                </HStack>

                {/* Plans */}
                <HStack space={6} justifyContent="center" width="100%">
                  {(billingCycle === "monthly"
                    ? [
                        {
                          id: "50GBM",
                          label: "50GB",
                          price: `$1.25 / ${Loc.currentLang.purchaseCredits.month}`,
                        },
                        {
                          id: "100GBM",
                          label: "100GB",
                          price: `$1.99 / ${Loc.currentLang.purchaseCredits.month}`,
                        },
                        {
                          id: "1TBM",
                          label: "1TB",
                          price: `$9.99 / ${Loc.currentLang.purchaseCredits.month}`,
                        },
                      ]
                    : [
                        {
                          id: "50GBY",
                          label: "50GB",
                          price: `$10 / ${Loc.currentLang.purchaseCredits.year}`,
                        },
                        {
                          id: "100GBY",
                          label: "100GB",
                          price: `$19 / ${Loc.currentLang.purchaseCredits.year}`,
                        },
                        {
                          id: "1TBY",
                          label: "1TB",
                          price: `$100 / ${Loc.currentLang.purchaseCredits.year}`,
                        },
                      ]
                  ).map((plan) => (
                    <VStack
                      key={plan.id}
                      space={2}
                      alignItems="center"
                      borderWidth={1}
                      borderRadius="lg"
                      borderColor="gray.300"
                      p={4}
                      minW="130px"
                    >
                      <Text fontSize="sm" color="gray.600">
                        {Loc.currentLang.purchaseCredits.getCaps}
                      </Text>

                      <Text bold fontSize="lg">
                        {plan.label}
                      </Text>

                      <Text fontSize="sm" color="gray.600">
                        {plan.price}
                      </Text>

                      <Button
                        onPress={() => handleBuyNowCredits(plan.id)}
                        bg="AVColor.primary"
                        _text={{ color: "white", fontWeight: "semibold" }}
                        rounded="full"
                        px={6}
                      >
                        {Loc.currentLang.purchaseCredits.buyNowCaps}
                      </Button>
                    </VStack>
                  ))}
                </HStack>
              </VStack>
            )}
          </HStack>

          {/* Amount Input */}
          {showOtherAmount && (
            <Box mb={6}>
              <AVInputtext
                type="text"
                label={Loc.currentLang.purchaseCredits.enterAmount}
                onChange={(e) => {
                  let input = e.target.value;

                  // Allow numbers + one decimal point
                  input = input
                    .replace(/[^0-9.]/g, "") // remove invalid chars
                    .replace(/(\..*)\./g, "$1"); // allow only one decimal

                  // Limit to 2 decimal places
                  const parts = input.split(".");
                  if (parts[1]?.length > 2) {
                    input = `${parts[0]}.${parts[1].slice(0, 2)}`;
                  }

                  setCustomAmount(input);

                  const amount = Number(input);

                  if (!isNaN(amount) && amount > 0) {
                    const credits = amount * 5000 - 2000;
                    setSelectAmtWithCredits(credits.toString());
                    setIsMinValidate(amount < 1);
                  } else {
                    setSelectAmtWithCredits("0");
                    setIsMinValidate(true);
                  }
                }}
                value={customAmount}
                height={"15px"}
                defaultValue={undefined}
                width={undefined}
                minRows={undefined}
                multiline={undefined}
                maxLength={4}
                size={undefined}
                placeholder={undefined}
              />

              {isMinValidate && (
                <Text
                  style={{
                    color: "#FF0000",
                    fontSize: 12,
                    marginTop: 4,
                    marginLeft: 4,
                  }}
                >
                  {Loc.currentLang.purchaseCredits.minimumAmountOne}
                </Text>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          {!isBuyStorage && (
            <HStack
              space={5}
              justifyContent="center"
              alignSelf="center"
              flexWrap="wrap"
              maxWidth="100%"
            >
              <Button
                width="140px"
                rounded="full"
                height="33px"
                paddingTop="5px"
                paddingBottom="5px"
                backgroundColor="gray.300"
                onPress={() => {
                  setShowCreditsPopup(false);
                  setTriggerBuyCredits?.(false);
                }}
              >
                <Text fontSize="16px" color="black">
                  {Loc.currentLang.menu.cancel}
                </Text>
              </Button>

              <Button
                rounded="full"
                width="140px"
                height="33px"
                paddingTop="5px"
                paddingBottom="5px"
                backgroundColor="AVColor.primary"
                isLoading={isLoadingBuy}
                isDisabled={
                  isLoadingBuy ||
                  isMinValidate ||
                  (selectedAmount === Loc.currentLang.activity.other &&
                    !customAmount &&
                    !isMinValidate)
                }
                onPress={() => handleBuyNowCredits()}
              >
                <Text fontSize="14px" color="white">
                  {Loc.currentLang.purchaseCredits.purchaseCredits}
                </Text>
              </Button>
            </HStack>
          )}
        </VStack>
      </div>
    </Box>
  );
};

export default PurchaseCreditsPopup;
